// The shapes the Timesheets API returns (apps/api/src/routes/timesheets.js
// shiftView). Every minute figure is computed server-side by shift-math.

export interface ShiftSession {
  id: string
  team_id: string
  team_name: string
  start_date: string
  start_time: string
  finish_date: string | null
  finish_time: string | null
  finish_next_day: boolean
  duration_minutes: number | null
  issues: string[]
  // D6.13: the zone this session's local times were read in, where known.
  zone_name?: string | null
  zone_source?: 'site' | 'tenant' | 'device' | 'unresolved'
  zone_label?: string
  zone_proven?: boolean
  // Actual elapsed minutes from the two instants; null when the zone is unrecorded.
  elapsed_minutes?: number | null
}

// GET /api/timesheets/zone: the zone a new local-time entry is read in.
export interface EntryZone {
  zone: string | null
  source: 'site' | 'tenant' | null
  source_label?: string
  site_id?: string | null
  site_name?: string | null
  fell_back_from_site?: boolean
  // True when the zone is the one this shift's own sessions proved (a lookup with shift_id only).
  inherited_from_shift?: boolean
  // Why a person must confirm this zone before a time is read in it.
  needs_confirmation?: string[]
  problem: { code: string; message: string } | null
  sites: { id: string; name: string; timezone: string | null }[]
}

export interface WallInterval {
  start: string
  end: string
  minutes?: number
}

export interface ShiftMetrics {
  span_minutes: number
  recorded_minutes: number
  raw_minutes: number
  gap_minutes: number
  overlap_minutes: number
  gaps: WallInterval[]
  intervals: WallInterval[]
  overlapping_session_ids: string[]
  open_session_ids: string[]
  invalid_session_ids: string[]
  incomplete: boolean
}

export interface ShiftEvent {
  id: string
  type: 'adjustment' | 'comment'
  actor: string
  actor_email: string | null
  timestamp: string
  reason: string
  changes: { f: string; from: string; to: string }[]
  reverses?: string
  can_undo?: boolean
}

// What the phone recorded (a Crew clock-on, a move, a clock-off) sits in the
// same history as the office's edits, but it is not an adjustment: only an
// edit made in Control is.
export const isCrewEntry = (e: ShiftEvent) => e.type !== 'comment' && e.actor === 'Dataphyll Crew' && !e.actor_email
export const isAdjustment = (e: ShiftEvent) => e.type !== 'comment' && !isCrewEntry(e)

export interface CapturedRecord {
  sessions: { team_id: string; start_date: string; start_time: string; finish_date: string | null; finish_time: string | null }[]
  breaks?: { paid_minutes: number; unpaid_minutes: number }
}

export interface Shift {
  id: string
  human_id: string
  worker_name: string
  work_date: string
  exclude_from_wages: boolean
  captured: CapturedRecord | null
  sessions: ShiftSession[]
  session_count: number
  team_ids: string[]
  team_names: string[]
  metrics?: ShiftMetrics
  span_minutes: number
  recorded_minutes: number
  gap_minutes: number
  overlap_minutes: number
  paid_minutes: number
  unpaid_minutes: number
  paid_is_manual: boolean
  unpaid_is_manual: boolean
  breaks?: { computed: { paid_minutes: number; unpaid_minutes: number } }
  unpaid_deduction_minutes?: number
  net_payable_minutes: number
  has_overlap: boolean
  // h 1755-1756: minutes this shift's sessions share with the same worker's
  // shift on another work date.
  cross_date_overlaps?: { work_date: string; minutes: number }[]
  incomplete?: boolean
  provisional?: boolean
  needs_attention: boolean
  adjustment_history?: ShiftEvent[]
  adjustment_count: number
  day_state?: DayStateName
  dst?: ShiftDst
}

// D6.13: whether this shift crosses a clock change, and the normal-hours input
// for a spring crossing. Wall, elapsed and entered normal hours are kept apart;
// none of them is a payable figure.
export interface ShiftDst {
  affected: boolean
  kind: 'spring' | 'autumn' | 'spring_and_autumn' | null
  unknown_sessions: number
  region?: string | null
  wall_minutes?: number
  elapsed_minutes?: number
  can_enter_normal_hours?: boolean
  normal_minutes?: number | null
  normal_supplied_by?: string | null
  normal_supplied_at?: string | null
  normal_note?: string | null
  export_status?: { code: string; message: string } | null
  // Whether the wage calculation has used the entered hours (spring crossing in a supported region only).
  calculation?: ShiftDstCalculation | null
}

export type ShiftDstCalculation =
  | { state: 'not_entered' }
  | { state: 'waiting' }
  | { state: 'cannot_allocate'; message?: string | null }
  | { state: 'applied'; payable_minutes: number; adjustment_minutes: number; clock_change_minutes: number | null }

export type DayStateName = 'LIVE' | 'CLOSED' | 'LOCKED'

export interface Team {
  id: string
  name: string
  colour?: string | null
  active?: boolean
  is_induction?: boolean
}

export interface Person {
  id: string
  first_name: string
  last_name: string
  preferred_name?: string | null
  archived?: boolean
}

export const DEFAULT_TEAM_COLOUR = '#a89e8c'

export function isProvisional(s: Shift) {
  return s.provisional ?? (!!s.incomplete || s.overlap_minutes > 0)
}
